import type { Locale as DateFnsLocale } from 'date-fns';
import { computed, ref } from 'vue';
import { cy, enGB, enUS, es, fr } from 'date-fns/locale';
import config from '@/config';
import { i18n } from '@/utils/i18n';
import { brandLocales, resolveLocale } from '@/locale';

// Everything a locale implies beyond its translated messages. A single BCP 47
// tag selects the whole set, so message content and date/time formatting
// cannot drift apart the way independent config fields could.
export type LocaleDefinition = {
  // The language's own name for itself. Locale pickers conventionally use
  // endonyms, so a speaker can find their language without reading the one
  // that is currently active.
  name: string;
  // Distinguishes variants of a language, and is omitted where the tag makes no
  // regional commitment.
  region?: string;
  dateFns: DateFnsLocale;
  // The language to ask address search providers for. Not every locale the app
  // offers is one they support, and both Google Places and Loqate reject a
  // language they do not recognise rather than falling back, so a locale they
  // cannot serve names the closest one they can.
  addressSearchLanguage: string;
  // Which system distances are shown in. There is no reliable way to derive
  // this from the tag: the browser API that reports a locale's measurement
  // system is not available on every platform the app runs on.
  distanceUnits: 'metric' | 'imperial';
  // Numeric date as a date-fns pattern.
  dateShort: string;
  use24Hour: boolean;
};

const FALLBACK_TAG = 'en-GB';

// Declaration order is picker order.
export const localeDefinitions: Record<string, LocaleDefinition> = {
  'en-GB': {
    name: 'English',
    region: 'UK',
    dateFns: enGB,
    addressSearchLanguage: 'en-GB',
    distanceUnits: 'metric',
    dateShort: 'dd/MM/yyyy',
    use24Hour: true,
  },
  'en-US': {
    name: 'English',
    region: 'US',
    dateFns: enUS,
    addressSearchLanguage: 'en-US',
    distanceUnits: 'imperial',
    dateShort: 'MM/dd/yyyy',
    use24Hour: false,
  },
  cy: {
    name: 'Cymraeg',
    dateFns: cy,
    // Neither address provider supports Welsh, so results come back in the
    // English of the country Welsh is spoken in.
    addressSearchLanguage: 'en-GB',
    distanceUnits: 'metric',
    dateShort: 'dd/MM/yyyy',
    use24Hour: true,
  },
  fr: {
    name: 'Français',
    dateFns: fr,
    addressSearchLanguage: 'fr',
    distanceUnits: 'metric',
    dateShort: 'dd/MM/yyyy',
    use24Hour: true,
  },
  'es-US': {
    name: 'Español',
    region: 'US',
    // date-fns has no US Spanish; its 'es' supplies day and month names, and
    // the conventions that differ are held here rather than in the date-fns
    // locale.
    dateFns: es,
    // Neither address provider recognises 'es-US'. Both accept plain 'es',
    // which differs from the Spanish this locale is written in only in wording
    // that place names do not carry.
    addressSearchLanguage: 'es',
    // US numeric conventions with Spanish wording, the same relationship Welsh
    // has to en-GB. CLDR would give this locale a day-first date and metric
    // distances, but the two locales are offered side by side in one US
    // deployment, and a date that reads 5/11 in one language and 11/5 in the
    // other is a misreading waiting to happen.
    distanceUnits: 'imperial',
    dateShort: 'MM/dd/yyyy',
    use24Hour: false,
  },
};

// A plain module-level ref rather than composable state: over half the
// consumers of the active locale are plain modules rather than components,
// and those cannot call a composable. Refs work anywhere.
const activeTag = ref(resolveLocale(config.app.defaultLocale));

export const localeFormats = computed(
  () => localeDefinitions[activeTag.value] ?? localeDefinitions[FALLBACK_TAG],
);

// What the active brand can actually offer: a locale needs both translated
// messages (a brand locale directory) and a definition here, since the picker
// needs a name and formatting needs a date-fns locale.
export const availableLocales = computed(() =>
  Object.entries(localeDefinitions)
    .filter(([tag]) => brandLocales.includes(tag))
    .map(([tag, definition]) => ({ tag, ...definition })),
);

if (import.meta.env.DEV) {
  const undefinedLocales = brandLocales.filter(
    (tag) => !(tag in localeDefinitions),
  );

  if (undefinedLocales.length) {
    console.warn(
      `Locale directories with no definition in utils/locale, so not offered: ${undefinedLocales.join(', ')}`,
    );
  }
}

export const getLocaleTag = (): string => activeTag.value;

// The only way to change locale. Moves the translated messages and the
// formatting rules together, and returns the tag actually adopted, which may
// differ from the one requested if the brand does not offer it.
export const setLocale = (tag: string): string => {
  const resolved = resolveLocale(tag);

  activeTag.value = resolved;
  i18n.global.locale.value = resolved;

  return resolved;
};
